import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { DockviewReact } from "dockview";
import { themeLightSpaced, themeAbyssSpaced } from "dockview";
import PositionTable from "./PositionTable";
import OrderTable from "./OrderTable";
import AlertTableView from "./AlertTableView";
import AlertWindow from "./AlertWindow";
import Note from "./NotesPed";
import FloatingMenu from "./FloatingMenu";
import { useWebSocket, sendGlobalWS } from "../../context/UseWebSocketContext";

import "dockview/dist/styles/dockview.css";
import "../cssstyle/dockview.css";

export default function Workspace({ darkMode = false, showAddPanel = true }) {
  const dockviewRef = useRef(null);
  const saveTimeoutRef = useRef(null);
  const [isWsConnected, setIsWsConnected] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const { socketRef } = useWebSocket();

  // Check WebSocket connection
  useEffect(() => {
    const checkConnection = setInterval(() => {
      const isConnected = socketRef?.current?.readyState === WebSocket.OPEN;
      setIsWsConnected(isConnected);
      if (isConnected) {
        console.log("✅ WebSocket is connected");
      }
    }, 3000);
    return () => clearInterval(checkConnection);
  }, [socketRef]);

  const selectedTheme = darkMode ? themeAbyssSpaced : themeLightSpaced;

  const maximizePanel = useCallback((panelId) => {
    const api = dockviewRef.current;
    if (!api) return;
    api.panels.forEach((p) => {
      if (p.id !== panelId) p.api.setVisible(false);
    });
    saveLayout();
  }, []);

  const restorePanels = useCallback(() => {
    const api = dockviewRef.current;
    if (!api) return;
    api.panels.forEach((p) => p.api.setVisible(true));
    saveLayout();
  }, []);

  const saveLayout = useCallback(() => {
    if (!dockviewRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      try {
        const layout = dockviewRef.current.toJSON();
        const currentTheme = darkMode ? "abyss-spaced" : "light-spaced";
        localStorage.setItem("dockview-layout", JSON.stringify({ ...layout, theme: currentTheme, lastSaved: new Date().toISOString() }));
      } catch (error) {
        console.error("Error saving layout:", error);
      }
    }, 500);
  }, [darkMode]);

  // Reset Layout with automatic data refresh
  const handleResetLayout = useCallback(() => {
    const api = dockviewRef.current;
    if (!api || isResetting) return;
    
    setIsResetting(true);
    console.log("🔄 Resetting layout...");
    
    localStorage.removeItem("dockview-layout");
    
    // Remove all existing panels
    const allPanels = [...api.panels];
    allPanels.forEach((panel) => {
      api.removePanel(panel);
    });

    // Create new panels
    const tradeNowPanel = api.addPanel({ 
      id: "tradenow", 
      component: "TradeNow", 
      title: "📈 Trade Now" 
    });
    
    const alertPanel = api.addPanel({ 
      id: "alert", 
      component: "Alert", 
      title: "🔔 Alert", 
      position: { referencePanel: tradeNowPanel, direction: "right" } 
    });
    
    const orderPanel = api.addPanel({ 
      id: "order", 
      component: "Order", 
      title: "📊 Order", 
      position: { referencePanel: alertPanel, direction: "right" } 
    });
    
    api.addPanel({ 
      id: "position", 
      component: "Position", 
      title: "💼 Position", 
      position: { referencePanel: orderPanel, direction: "below" } 
    });

    // Save layout and refresh data
    setTimeout(() => {
      saveLayout();
      
      // Force refresh all data
      if (isWsConnected) {
        console.log("🔄 Refreshing all data after layout reset...");
        sendGlobalWS({ r: "orders" });
        sendGlobalWS({ r: "positions" });
        
        // Get current date for alerts
        const today = new Date().toISOString().split("T")[0];
        sendGlobalWS({ r: "alerts", arg: today });
      }
      
      setIsResetting(false);
    }, 300);
  }, [saveLayout, isWsConnected, isResetting]);

  // ✅ FIXED: Components - All are now self-dependent, no props needed except darkMode
  const components = useMemo(() => ({
    TradeNow: () => <AlertWindow darkMode={darkMode} restorePanels={restorePanels} />,
    Order: () => <OrderTable darkMode={darkMode} />,
    Position: () => <PositionTable darkMode={darkMode} />,
    Alert: () => <AlertTableView darkMode={darkMode} />,
    Note: () => <Note darkMode={darkMode} />,
  }), [darkMode, restorePanels]);

  const onReady = useCallback((event) => {
    const api = event.api;
    dockviewRef.current = api;
    const savedLayout = localStorage.getItem("dockview-layout");

    if (savedLayout) {
      try {
        const parsed = JSON.parse(savedLayout);
        api.fromJSON(parsed);
        console.log("✅ Layout loaded from storage");
      } catch (e) {
        console.log("⚠️ Error loading layout, creating default");
        createDefaultLayout(api);
      }
    } else {
      createDefaultLayout(api);
    }

    api.onDidLayoutChange(() => saveLayout());
    api.onDidAddPanel(() => saveLayout());
    api.onDidRemovePanel(() => saveLayout());
    
    // Load initial data when ready
    setTimeout(() => {
      if (isWsConnected) {
        console.log("🔄 Loading initial data...");
        sendGlobalWS({ r: "orders" });
        sendGlobalWS({ r: "positions" });
        const today = new Date().toISOString().split("T")[0];
        sendGlobalWS({ r: "alerts", arg: today });
      }
    }, 500);
  }, [saveLayout, isWsConnected]);

  const createDefaultLayout = useCallback((api) => {
    const tradeNowPanel = api.addPanel({ id: "tradenow", component: "TradeNow", title: "📈 Trade Now" });
    const alertPanel = api.addPanel({ id: "alert", component: "Alert", title: "🔔 Alert", position: { referencePanel: tradeNowPanel, direction: "right" } });
    const orderPanel = api.addPanel({ id: "order", component: "Order", title: "📊 Order", position: { referencePanel: alertPanel, direction: "right" } });
    api.addPanel({ id: "position", component: "Position", title: "💼 Position", position: { referencePanel: orderPanel, direction: "below" } });
    setTimeout(() => saveLayout(), 100);
  }, [saveLayout]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const panel = e.dataTransfer.getData("panel");
    if (panel && dockviewRef.current) {
      dockviewRef.current.addPanel({ id: panel + Date.now(), component: panel, title: panel });
      setTimeout(() => saveLayout(), 100);
    }
  }, [saveLayout]);

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e) => { 
      if (e.key === "Escape") restorePanels(); 
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [restorePanels]);

  // Cleanup
  useEffect(() => {
    return () => { 
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); 
    };
  }, []);

  // Auto refresh data every 30 seconds
  useEffect(() => {
    if (!isWsConnected) return;
    const intervalId = setInterval(() => {
      console.log("🔄 Auto refreshing data...");
      sendGlobalWS({ r: "orders" });
      sendGlobalWS({ r: "positions" });
      const today = new Date().toISOString().split("T")[0];
      sendGlobalWS({ r: "alerts", arg: today });
    }, 30000);
    return () => clearInterval(intervalId);
  }, [isWsConnected]);

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }} onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
      <DockviewReact components={components} onReady={onReady} theme={selectedTheme} hideBorders={true} />
      <FloatingMenu activeTradeCopier="admin" onResetLayout={handleResetLayout} darkMode={darkMode} />
    </div>
  );
}